import { Box, Chip, Divider, Typography } from '@mui/material'
import { VerifiedUser } from '@mui/icons-material'

/**
 * What an ARIAS certificate looks like, shown rather than described.
 *
 * The locked certificates page used to render a blurred table and a sentence
 * defining what a certificate is. A reader who does not already want one
 * learned nothing: the blur hid the only thing that makes the feature feel
 * valuable, which is the DETAIL — that we record the model, the tools, the data
 * reach and the delegation targets, and compare all of them on every later
 * scan.
 *
 * So this is a specimen, in focus, with the fields filled in.
 *
 * Why a specimen does not break the no-fabricated-output rule
 * -----------------------------------------------------------
 * The rule exists because invented counts and identifiers imply first-hand data
 * we do not have, and collapse the moment a prospect probes them. A blurred
 * skeleton with numbers behind it is the worst case — it reads as a real result
 * with a soft edge.
 *
 * A specimen is the opposite, and the difference is whether it could ever be
 * mistaken for a measurement:
 *
 *   - every value is obviously generic (`checkout-assistant`, `acme/storefront`)
 *     rather than plausibly someone's
 *   - the word SPECIMEN is on the document, in readable text, so a screenshot
 *     cannot lose it
 *   - no count anywhere describes the reader's estate
 *
 * This is how a passport office shows a sample passport. It is not a claim
 * about anybody.
 *
 * Deliberately NOT blurred. The blur is right where the shape is the message
 * (a table of routes) and wrong where the content is the message: a certificate
 * is persuasive because of what is on it.
 */
const FIELDS: Array<[string, string]> = [
  ['Agent', 'checkout-assistant'],
  ['Repository', 'acme/storefront'],
  ['Framework', 'langgraph'],
  ['Model', 'claude-sonnet-4-5 (anthropic)'],
  ['Tools at certification', 'read_catalogue, write_order, refund_payment'],
  ['Data reached', 'orders-pgsql, product-index (vector), customer-docs (s3)'],
  ['Delegates to', 'fraud-check, inventory-agent'],
  ['Memory', 'conversation buffer, persisted'],
  ['Fingerprint', 'abf 2.2 · composite a41f…9c02'],
]

const CertificateSpecimen = () => (
  <Box
    sx={{
      border: 1,
      borderColor: 'divider',
      borderRadius: 2,
      p: 2,
      bgcolor: 'background.paper',
      maxWidth: 520,
      mx: 'auto',
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    {/* Readable, not behind the blur, so it survives a screenshot. */}
    <Typography
      aria-hidden="true"
      sx={{
        position: 'absolute',
        top: 28,
        right: -28,
        transform: 'rotate(-20deg)',
        fontSize: 44,
        fontWeight: 900,
        letterSpacing: 4,
        color: 'action.hover',
        pointerEvents: 'none',
        userSelect: 'none',
      }}
    >
      SPECIMEN
    </Typography>

    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
      <VerifiedUser color="success" sx={{ fontSize: 20 }} />
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        Agent Behaviour Certificate
      </Typography>
      <Chip label="Active" size="small" color="success" sx={{ height: 18, fontSize: '0.65rem' }} />
    </Box>
    <Typography variant="caption" color="text.secondary">
      Specimen — example values, not your estate
    </Typography>

    <Divider sx={{ my: 1.25 }} />

    {FIELDS.map(([label, value]) => (
      <Box key={label} sx={{ display: 'flex', gap: 1, mb: 0.6 }}>
        <Typography
          variant="caption"
          sx={{ minWidth: 150, color: 'text.secondary', flexShrink: 0 }}
        >
          {label}
        </Typography>
        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.68rem' }}>
          {value}
        </Typography>
      </Box>
    ))}

    <Divider sx={{ my: 1.25 }} />

    {/* The sentence that makes the document matter: it is not a record, it is
        the thing every later scan is measured against. */}
    <Typography variant="caption" color="text.secondary">
      Every later scan is compared against this record. A new tool, a model
      swap, a widened data reach or a new delegation target raises a verdict
      against the certificate instead of passing unnoticed.
    </Typography>
  </Box>
)

export default CertificateSpecimen
